import React, { useState, useEffect, useContext } from "react";
import { StoreContext } from "./context/StoreContext";
import { ConsoleLogger, Hub } from "aws-amplify/utils";
import { graphql, graphqlOperation } from "./modules/AmplifyServices";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { Cache } from "aws-amplify/utils";
import { listProviderSchedules } from "./graphql/queries";
import { getMbxUser } from "./queries/UserQueries";
import { updateUser } from "./graphql/mutations";
import {
    Route,
    Routes,
    Navigate,
    useLocation,
    useNavigate
} from "react-router-dom";
import { createBrowserHistory } from "history";
import IntegrationsPage from "./pages/IntegrationsPage";
import "./App.css";
import MainContainer from "./components/MainContainer";
import * as queries from "./graphql/queries";
import OnboardingApp from "./onboarding/OnboardingApp";
import CheckoutApp from "./pages/checkout/CheckoutApp";
import ActivateAccountApp from "./pages/checkout/components/pages/ActivateAccountApp";
import { CustomSignIn } from "./auth/CustomSignIn";
import { useWindowSize } from "react-use";
import { colors } from "./utils/Constants";
import {
    ThemeProvider,
    StyledEngineProvider,
    createTheme
} from "@mui/material";
import { BookingsPage } from "./components/bookingsTable/BookingsPage";
import { ServicesPage } from "./components/servicesTable/ServicesPage";
import { UsersPage } from "./components/usersTable/UsersPage";
import { WebhooksPage } from "./components/webhooksTable/WebhooksPage";
import { getUserPrimaryColor } from "./user/UserCommon";
import { Helmet } from "react-helmet";
import * as Sentry from "@sentry/react";
import { getUserFromCache } from "./user/UserCommon";
import chroma from "chroma-js";
import GoogleFontLoader from "./components/GoogleFontLoader";
import { GOOGLE_FONTS } from "./utils/Constants";
import { PrivateRoutes } from "./utils/CommonComponents/ProtectedRoute";
import CircularProgress from "@mui/material/CircularProgress";
import {
    mbxUserAtom,
    isMbxUserAdmin,
    isMbxUserProvider,
    isMbxUserClient,
    isMbxUserActive,
    allUsersAtom,
    allSchedulesAtom,
    isMbxUserMbxAdmin,
    isMbxUserVerifiedStaff,
    isMapAnalysisAllowedAtom
} from "./atoms/atoms";
import { selectedTabAtom } from "./atoms/bookingsTable";
import { selectedUserIdAtom } from "./atoms/usersTable";
import { useAtom } from "jotai";
import ExpiredSubscriptionModal from "./components/ExpiredSubscriptionModal";
import { ProviderScheduleDevEx } from "./forms/ProviderScheduleDevEx";
import CalendarPage from "./components/calendar/CalendarPage";
import AuditRecordsForm from "./forms/AuditRecordsForm";
import PotentialSalesForm from "./forms/PotentialSalesForm";
import BillingForm from "./forms/BillingForm";
import GeoAnalysis from "./forms/GeoAnalysis";
import UsersEditViewForm from "./forms/UsersEditViewForm";
import AdditionalChargesForm from "./forms/AdditionalChargesForm";
import { SkillsForm } from "./forms/SkillsForm";
import { SkillsAddEditForm } from "./forms/SkillsAddEditForm";
import MiscBillingTransaction from "./billing/MiscBillingTransaction";
import ServiceForm from "./forms/ServiceForm";
import PromoCodes from "./components/promoCodes/PromoCodes";
import { CompanyLocationForm } from "./forms/CompanyLocationForm";
import ServicePageTabs from "./forms/ServicePageTabs";
import { RegionForm } from "./forms/RegionForm";
import PackageForm from "./forms/PackageForm";
import RegionAddEditForm from "./forms/RegionAddEditForm";
import CompanyLocationAddEditForm from "./forms/CompanyLocationAddEditForm";
import ServiceAdd from "./forms/ServiceAdd";
import ManageServiceCategoryForm from "./forms/ManageServiceCategoryForm";
import CategoryAddEditForm from "./forms/CategoryAddEditForm";
import ManageServiceOrder from "./components/serviceorder/ManageServiceOrder";
import { userHasMarketboxAdminRole } from "./user/UserCommon";
import isFeatureGated from "./modules/FeatureGatingModule";
import FeatureGateDialog from "./components/FeatureGateDialog";
import DashboardForm from "./forms/DashboardForm";
import { BookingsListForm } from "./forms/BookingsListForm";
import OrderForm from "./forms/OrderForm";
import ReportForm from "./forms/ReportForm";
import IntegrationForm from "./forms/IntegrationForm";
import CustomerForm from "./forms/CustomerForm";
import CompanyForm from "./forms/CompanyForm";
import ClientBillingInfo from "./forms/ClientBillingInfo";
import CompanySettingsModal from "./forms/CompanySettingsModal";
import { ProviderSchedule } from "./forms/ProviderSchedule";
import { FlagsContext } from "./context/flags";
import MaintenanceDialog from "./components/MaintenanceDialog";

export const history = createBrowserHistory();
export const UserContext = React.createContext();

const logger = new ConsoleLogger("App");
// redirects the browser to an external URL (react-router Navigate only handles internal routes)
function ExternalRedirect({ to }) {
    useEffect(() => {
        window.location.replace(to);
    }, [to]);
    return null;
}
// init sentry for exception tracing, project = marketbox-auth-site
Sentry.init({
    dsn: "https://712f9fadb3b7420eade0d692cdeb405f@o435799.ingest.sentry.io/5428731"
});

function App(props) {
    const navigate = useNavigate();
    const user = getUserFromCache();
    const regionalPricingFeatureGated = isFeatureGated(
        getUserFromCache()?.company?.subscriptionLevel,
        "RegionalPricing"
    );
    const canAccessPackages =
        user &&
        user.company &&
        !user.company.singleApptOnly &&
        !user.company.offersForeverAppt;

    const integrationsFeatureGated = isFeatureGated(
        getUserFromCache()?.company?.subscriptionLevel,
        "Integrations"
    );
    const featureFlags = useContext(FlagsContext);
    const { displayOngoingMaintenanceMessage } = featureFlags || {};

    const storedCognitoUser = localStorage.getItem("loggedInCongnitoUser");
    let storedParsedCognitoUser = null;
    try {
        storedParsedCognitoUser = JSON.parse(storedCognitoUser);
    } catch {}

    const [loggedInCongnitoUser, setLoggedInCongnitoUser] = useState(
        storedParsedCognitoUser
    );

    const { actions } = useContext(StoreContext);
    // false : user is not logged in , null : user logged in in cognito but not yet verified by our system
    const [userIsLoggedIn, setUserIsLoggedIn] = useState(
        loggedInCongnitoUser ? null : false
    );
    const isDesktop = useWindowSize().width >= 900;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [innerTheme, setInnerTheme] = useState(createTheme({}));

    const handleDrawerAction = () => {
        setDrawerOpen(!drawerOpen);
    };
    const [mbxUser, setMbxUser] = useAtom(mbxUserAtom);
    const [isMBAdmin] = useAtom(isMbxUserMbxAdmin);
    const [isMbxAdminLoggedIn] = useAtom(isMbxUserMbxAdmin);
    const [isAdminLoggedIn] = useAtom(isMbxUserAdmin);
    const [isVerifiedStaffLoggedIn] = useAtom(isMbxUserVerifiedStaff);
    const [isProviderLoggedIn] = useAtom(isMbxUserProvider);
    const [isClientLoggedIn] = useAtom(isMbxUserClient);
    const [isUserActive] = useAtom(isMbxUserActive);
    const [, setAllUsers] = useAtom(allUsersAtom);
    const [, setAllSchedules] = useAtom(allSchedulesAtom);
    const [, setSelectedTab] = useAtom(selectedTabAtom);
    const [, setSelectedUserId] = useAtom(selectedUserIdAtom);
    const [allowMapAnalysis] = useAtom(isMapAnalysisAllowedAtom);
    const location = useLocation();

    useEffect(() => {
        if (
            location &&
            location.pathname &&
            location.pathname === "/connectsuccess"
        ) {
            let stripeConnectFlowJson = localStorage.getItem(
                "stripeConnectFlowAdminApp"
            );
            localStorage.removeItem("stripeConnectFlowAdminApp");
            if (stripeConnectFlowJson) {
                let stripeConnectFlowData = JSON.parse(stripeConnectFlowJson);
                actions.setMode("Edit");
                actions.setPage("CompanyAddEditForm");
                actions.setId(stripeConnectFlowData.companyId);
            }
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        const abortController = new AbortController();
        if (mounted) {
            Hub.listen("auth", (data) => {
                const { payload } = data;
                onAuthEvent(payload);
                if (payload.data) {
                    logger.debug(
                        "A new auth event has happened: ",
                        payload.data.username + " has " + data.payload.event
                    );
                } else {
                    logger.debug("A new auth event has happened.");
                }
            });
        }

        const cleanup = () => {
            mounted = false;
            abortController.abort();
        };
        return cleanup;
    }, []);

    useEffect(() => {
        async function processUserAuthentication() {
            try {
                let loggedInCognUser = await getCurrentUser();
                localStorage.setItem(
                    "loggedInCongnitoUser",
                    JSON.stringify(loggedInCognUser)
                );
                // const { tokens: session } = await fetchAuthSession();
                // loggedInCognUser.session = session;
                await postUserAuthenticationProcessing(loggedInCognUser);
            } catch {
                localStorage.setItem("loggedInCongnitoUser", "");
            }
        }
        processUserAuthentication();
    }, []);

    async function postUserAuthenticationProcessing(loggedInCongnUser) {
        //get mbuser
        //ensure mbuser is active and not deleted
        //get clientid if client role
        //get primary color
        //set inner theme
        //
        try {
            let mbuser = await getMbUserFromDb(loggedInCongnUser); //this get mbuser, checks if active and sets clientid in cache for client role
            if (mbuser && mbuser !== null) {
                if (mbuser.role === "PROVIDER" || !isDesktop) {
                    const providerSchedules =
                        await getProviderScheduleRecords(mbuser);
                    setAllUsers([mbuser]);
                    setAllSchedules(providerSchedules);
                    setSelectedUserId(mbuser.id);
                }
                await doColor(); //sets primary color and theme
                setUserIsLoggedIn(true);

                //conditonally append the Beamer trial script
                if (
                    process.env.REACT_APP_AUTH_SITE_URL.includes("staging") &&
                    mbuser?.companyId === "dcb3f376-0b71-411d-9c86-bc3ec26ea05b"
                ) {
                    appendBeamerScript();
                }
            }
        } catch (e) {
            console.log("error in postUserAuthenticationProcessing ", e);
        }
    }

    async function doColor() {
        let primaryColor = await Cache.getItem("primaryColor");
        if (!primaryColor || primaryColor === "") {
            // not cached so get primary color from db
            //Alternatively - PendingCompany which has no color yet.
            try {
                primaryColor = await getUserPrimaryColor();
                //  Cache.setItem("primaryColor", primaryColor);
            } catch (e) {
                console.log("asdf color error!", e);
            }
            if (!primaryColor) {
                //Most likely a pending company. Supply a default vallue.
                primaryColor = colors.MB_INFO_COLOR_MAIN;
            }
        }
        const newTheme = createTheme({
            palette: {
                primary: {
                    veryLight: chroma(primaryColor).alpha(0.04).hex(),
                    light: chroma(primaryColor).alpha(0.1).hex(),
                    medium: chroma(primaryColor).alpha(0.25).hex(),
                    main: primaryColor
                },
                info: {
                    lightest: colors.MB_INFO_COLOR_LIGHTEST,
                    light: colors.MB_INFO_COLOR_LIGHT,
                    main: colors.MB_INFO_COLOR_MAIN
                },
                success: {
                    light: colors.MB_SUCCESS_COLOR_LIGHT,
                    main: colors.MB_SUCCESS_COLOR_MAIN,
                    dark: colors.MB_SUCCESS_COLOR_DARK
                },
                warning: {
                    main: colors.MB_WARNING_COLOR_MAIN,
                    dark: colors.MB_WARNING_COLOR_DARK
                },
                helperText: {
                    main: colors.MB_HELPERTEXT_COLOR_MAIN
                },
                inputBorder: {
                    main: colors.MB_INPUT_BORDER_COLOR_MAIN
                },
                caution: {
                    light: colors.MB_CAUTION_COLOR_LIGHT,
                    main: colors.MB_CAUTION_COLOR_MAIN
                },
                text: {
                    main: colors.MB_BODY_COLOR_MAIN,
                    disabled: colors.MB_DISABLED_MAIN
                },
                transparent: {
                    main: colors.MB_TRANSPARENT_MAIN
                },
                action: {
                    disabled: colors.MB_DISABLED_MAIN
                },
                grey: {
                    lightest: colors.MB_GREY_LIGHTEST,
                    lightOld: colors.MB_GREY_LIGHTEST,
                    light: colors.MB_GREY_LIGHT,
                    medium: colors.MB_GREY_MEDIUM,
                    main: colors.MB_BODY_COLOR_MAIN
                },
                icon: {
                    disabled: colors.MB_DISABLED_MAIN
                },
                verified: colors.MB_SUCCESS_COLOR_MAIN,
                featureGateStarColor: {
                    notice: {
                        main: "#fcaf17"
                    }
                },
                components: {
                    MuiTablePagination: {
                        styleOverrides: {
                            root: {},
                            toolbar: {
                                alignItems: "baseline"
                            }
                        }
                    },
                    MuiAlert: {
                        styleOverrides: {
                            colorSuccess: {
                                backgroundColor: colors.MB_SUCCESS_COLOR_MAIN
                            }
                        }
                    }
                }
            }
        });
        setInnerTheme(newTheme);
    }

    // for mobile devices collapse menu
    useEffect(() => {
        if (drawerOpen !== isDesktop) setDrawerOpen(isDesktop);
    }, [isDesktop]);

    const onAuthEvent = async (payload) => {
        switch (payload.event) {
            case "signedIn":
                await postUserAuthenticationProcessing(payload.data);
                setLoggedInCongnitoUser(payload.data);
                const userEmail = payload.data?.signInDetails?.loginId;
                break;
            case "signUp":
                break;
            case "signedOut":
                localStorage.setItem("loggedInCongnitoUser", "");
                localStorage.setItem("loggedInMbUser", "");
                setLoggedInCongnitoUser(null);
                setUserIsLoggedIn(false);
                Cache.setItem("primaryColor", null);
                break;
            default:
                return;
        }
    };

    const getMbUserFromDb = async (loggedInCognitoUser) => {
        //reads the user from database
        const getUserInput = {
            id: loggedInCognitoUser.username
        };
        const { data } = await graphql(
            graphqlOperation(getMbxUser, getUserInput)
        );
        setMbxUser(data.getUser);
        if (data.getUser) {
            if (!data.getUser.active || data.getUser.deleted) {
                // logout user
                await signOut();
                localStorage.removeItem("appState");
                localStorage.setItem("loggedInCongnitoUser", "");
                localStorage.setItem("loggedInMbUser", "");
                setUserIsLoggedIn(false);
                return null;
            } else {
                localStorage.setItem(
                    "loggedInMbUser",
                    JSON.stringify(data.getUser)
                );
                await Cache.setItem("user", data.getUser);
                await initializeLoginCount(data.getUser); //TODO: Pankaj check if this is required
                // if user is a client, lookup the client id
                const role = data.getUser.role;
                if (role === "CLIENT") {
                    // clear client id
                    Cache.setItem("ClientId", null);
                    await getClientFromUser(
                        data.getUser.id,
                        data.getUser.companyId
                    );
                }
                return data.getUser;
            }
        }
    };

    async function initializeLoginCount(userData) {
        try {
            const prevLoginInfo = JSON.parse(userData.LoginInfo);
            if (prevLoginInfo?.loginCount) {
                return;
            } else {
                const loginInfo = {
                    lastLoginDate: new Date(),
                    loginCount: 1
                };
                const LoginInfo = JSON.stringify(loginInfo);
                const updateMBUserInput = {
                    role: userData.role,
                    emailaddress: userData.emailaddress,
                    id: userData.id,
                    LoginInfo
                };
                await graphql(
                    graphqlOperation(updateUser, { input: updateMBUserInput })
                );
            }
        } catch (e) {
            console.log(e);
        }
    }

    async function getProviderScheduleRecords(user) {
        try {
            const {
                data: {
                    listProviderSchedules: { items: providerSchedules }
                }
            } = await graphql(
                graphqlOperation(listProviderSchedules, {
                    id: `C-${user.companyId}|P-${user.providerId}`,
                    scheduleinfo: { beginsWith: "S" },
                    filter: {
                        deleted: { ne: true }
                    },
                    limit: 100
                })
            );
            return providerSchedules;
        } catch (e) {
            console.log("Error: Unable to get provider schedules", e);
        }
    }

    async function getClientFromUser(userId, companyId) {
        const clientProfile = await graphql(
            graphqlOperation(queries.clientByUserId, {
                userId: userId,
                filter: { companyId: { eq: companyId } }
            })
        );
        if (
            clientProfile &&
            clientProfile.data &&
            clientProfile.data.clientByUserId &&
            clientProfile.data.clientByUserId.items.length
        ) {
            const clientId = clientProfile.data.clientByUserId.items[0].id;
            Cache.setItem("ClientId", clientId);
        } else Cache.setItem("ClientId", null);
    }

    function getCompanyTagline() {
        const loggedInUser = getUserFromCache();
        let tagline;
        if (loggedInUser?.company?.tagline?.length <= 0) {
            tagline = loggedInUser?.company?.name;
        } else {
            tagline = loggedInUser?.company?.tagline;
        }
        return tagline;
    }

    async function setMbUserInCache(u) {
        setMbxUser(u);
        await Cache.setItem("user", u);
        localStorage.setItem("loggedInMbUser", JSON.stringify(u));
    }

    const appendBeamerScript = () => {
        const configScript = document.createElement("script");
        configScript.innerHTML = `
            var beamer_config = {
            product_id : '${process.env.REACT_APP_BEAMER_CODE}' //DO NOT CHANGE: This is your product code on Beamer
            };
        `;

        const embedScript = document.createElement("script");
        embedScript.src = "https://app.getbeamer.com/js/beamer-embed.js";
        embedScript.type = "text/javascript";
        embedScript.defer = true;

        document.body.appendChild(configScript);
        document.body.appendChild(embedScript);
    };

    return (
        <UserContext.Provider value={loggedInCongnitoUser}>
            <GoogleFontLoader fonts={GOOGLE_FONTS} />
            <>
                <Helmet>
                    <title>{getCompanyTagline()}</title>
                </Helmet>
                <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={innerTheme}>
                        <Routes>
                            {userIsLoggedIn === null ? (
                                <Route element={CircularProgress} />
                            ) : (
                                <Route
                                    element={
                                        <PrivateRoutes
                                            isUserLoggedIn={Boolean(
                                                userIsLoggedIn
                                            )}
                                        />
                                    }
                                >
                                    <Route
                                        path="/"
                                        element={
                                            displayOngoingMaintenanceMessage &&
                                            !isMBAdmin ? (
                                                <Navigate to="/maintenance" />
                                            ) : isUserActive ? (
                                                <div
                                                    className={
                                                        isClientLoggedIn
                                                            ? "app-container-client"
                                                            : "app-container"
                                                    }
                                                >
                                                    <MainContainer
                                                        handleDrawerAction={
                                                            handleDrawerAction
                                                        }
                                                        open={drawerOpen}
                                                    />
                                                    {isVerifiedStaffLoggedIn &&
                                                        user?.company
                                                            ?.setupCompleted &&
                                                        location.pathname ===
                                                            "/" &&
                                                        navigate("/dashboard")}
                                                    {isAdminLoggedIn &&
                                                        !user?.company
                                                            ?.setupCompleted &&
                                                        location.pathname ===
                                                            "/" &&
                                                        navigate("/dashboard")}
                                                    {!isAdminLoggedIn &&
                                                        location.pathname ===
                                                            "/" &&
                                                        navigate("/bookings")}
                                                </div>
                                            ) : (
                                                <Navigate to="/expired" />
                                            )
                                        }
                                    >
                                        <Route
                                            path="dashboard"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <DashboardForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="orders"
                                            element={
                                                isVerifiedStaffLoggedIn ? (
                                                    <OrderForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        {location.pathname ===
                                        "/new-bookings/upcoming"
                                            ? setSelectedTab("upcoming")
                                            : location.pathname ===
                                                "/new-bookings/past"
                                              ? setSelectedTab("past")
                                              : setSelectedTab("today")}
                                        {(location.pathname ===
                                            "/new-bookings/today" ||
                                            location.pathname ===
                                                "/new-bookings/upcoming" ||
                                            location.pathname ===
                                                "/new-bookings/past") && (
                                            <Route
                                                path={location.pathname}
                                                element={<BookingsPage />}
                                            />
                                        )}
                                        <Route
                                            path="new-bookings"
                                            element={<BookingsPage />}
                                        />
                                        <Route
                                            path="bookings"
                                            element={<BookingsListForm />}
                                        />
                                        <Route
                                            path="reports"
                                            element={
                                                isVerifiedStaffLoggedIn ? (
                                                    <ReportForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="integrations"
                                            element={
                                                isAdminLoggedIn ? (
                                                    integrationsFeatureGated ? (
                                                        <FeatureGateDialog
                                                            open={true}
                                                            handleOnClose={() => {
                                                                navigate(
                                                                    "/bookings"
                                                                );
                                                            }}
                                                        />
                                                    ) : (
                                                        <IntegrationForm />
                                                    )
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="webhooks"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <WebhooksPage />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="users"
                                            element={
                                                isVerifiedStaffLoggedIn ? (
                                                    <UsersPage />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="calendar"
                                            element={
                                                isVerifiedStaffLoggedIn ||
                                                isProviderLoggedIn ? (
                                                    <CalendarPage />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="old-calendar"
                                            element={
                                                isVerifiedStaffLoggedIn &&
                                                isDesktop ? (
                                                    <ProviderScheduleDevEx />
                                                ) : isProviderLoggedIn ||
                                                  (isVerifiedStaffLoggedIn &&
                                                      !isDesktop) ? (
                                                    <ProviderSchedule
                                                        company={user?.company}
                                                    />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="clients"
                                            element={
                                                isVerifiedStaffLoggedIn ? (
                                                    <CustomerForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="settings"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <CompanySettingsModal
                                                        open={true}
                                                    />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="companies"
                                            element={
                                                isMbxAdminLoggedIn ? (
                                                    <CompanyForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        >
                                            <Route
                                                path="create"
                                                element={
                                                    userHasMarketboxAdminRole() && (
                                                        <CompanySettingsModal
                                                            open={true}
                                                            onClose={() => {
                                                                navigate(
                                                                    "/companies"
                                                                );
                                                            }}
                                                        />
                                                    )
                                                }
                                            />
                                            <Route
                                                path="edit"
                                                element={
                                                    userHasMarketboxAdminRole && (
                                                        <CompanySettingsModal
                                                            open={true}
                                                            onClose={() => {
                                                                navigate(
                                                                    "/companies"
                                                                );
                                                            }}
                                                        />
                                                    )
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="profile"
                                            element={
                                                isClientLoggedIn ? (
                                                    <UsersEditViewForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        ></Route>
                                        <Route
                                            path="audit-trail"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <AuditRecordsForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="waitlist"
                                            element={
                                                isVerifiedStaffLoggedIn ? (
                                                    <PotentialSalesForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="billing-charges"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <BillingForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="map"
                                            element={
                                                allowMapAnalysis ? (
                                                    <GeoAnalysis />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="additional-charges"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <AdditionalChargesForm />
                                                ) : (
                                                    <Navigate to="/additional-charges" />
                                                )
                                            }
                                        />
                                        <Route
                                            path="skills"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <SkillsForm />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        >
                                            <Route
                                                path="create"
                                                element={
                                                    <SkillsAddEditForm mode="Add" />
                                                }
                                            />
                                            <Route
                                                path="edit"
                                                element={
                                                    <SkillsAddEditForm mode="Edit" />
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="processcharge"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <MiscBillingTransaction />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        />
                                        <Route path="new-services">
                                            <Route
                                                index
                                                element={
                                                    isAdminLoggedIn ? (
                                                        <ServicesPage />
                                                    ) : (
                                                        <Navigate to="/bookings" />
                                                    )
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="services/"
                                            element={
                                                isAdminLoggedIn ? (
                                                    <ServicePageTabs />
                                                ) : (
                                                    <Navigate to="/bookings" />
                                                )
                                            }
                                        >
                                            <Route
                                                index
                                                element={<ServicesPage />}
                                            />
                                            <Route
                                                path="order"
                                                element={<ManageServiceOrder />}
                                            />
                                            <Route path="skills">
                                                <Route
                                                    index
                                                    element={<SkillsForm />}
                                                />
                                                <Route
                                                    path="create"
                                                    element={
                                                        <SkillsAddEditForm mode="Add" />
                                                    }
                                                />
                                                <Route
                                                    path="edit"
                                                    element={
                                                        <SkillsAddEditForm mode="Edit" />
                                                    }
                                                />
                                            </Route>
                                            <Route path="categories">
                                                <Route
                                                    index
                                                    element={
                                                        <ManageServiceCategoryForm />
                                                    }
                                                />
                                                <Route
                                                    path="create"
                                                    element={
                                                        <CategoryAddEditForm mode="Add" />
                                                    }
                                                />
                                                <Route
                                                    path="edit"
                                                    element={
                                                        <CategoryAddEditForm mode="Edit" />
                                                    }
                                                />
                                            </Route>
                                            <Route path="locations">
                                                <Route
                                                    index
                                                    element={
                                                        <CompanyLocationForm />
                                                    }
                                                />
                                                <Route
                                                    path="create"
                                                    element={
                                                        <CompanyLocationAddEditForm mode="Add" />
                                                    }
                                                />
                                                <Route
                                                    path="edit"
                                                    element={
                                                        <CompanyLocationAddEditForm mode="Edit" />
                                                    }
                                                />
                                            </Route>
                                            <Route
                                                path="packages"
                                                element={<PackageForm />}
                                            />
                                            <Route
                                                path="promo-codes"
                                                element={
                                                    <PromoCodes
                                                        company={user?.company}
                                                        user={user}
                                                    />
                                                }
                                            />
                                            <Route path="regional-pricing">
                                                <Route
                                                    index
                                                    element={
                                                        regionalPricingFeatureGated ? (
                                                            <FeatureGateDialog
                                                                open={true}
                                                                handleOnClose={() => {
                                                                    navigate(
                                                                        "/services"
                                                                    );
                                                                }}
                                                            />
                                                        ) : (
                                                            <RegionForm />
                                                        )
                                                    }
                                                />
                                                <Route
                                                    path="create"
                                                    element={
                                                        regionalPricingFeatureGated ? (
                                                            <Navigate to="/services" />
                                                        ) : (
                                                            <RegionAddEditForm mode="Add" />
                                                        )
                                                    }
                                                />
                                                <Route
                                                    path="edit"
                                                    element={
                                                        regionalPricingFeatureGated ? (
                                                            <Navigate to="/services" />
                                                        ) : (
                                                            <RegionAddEditForm mode="Edit" />
                                                        )
                                                    }
                                                />
                                            </Route>
                                            ;
                                        </Route>
                                    </Route>
                                    <Route
                                        path="/connectsuccess"
                                        element={<Navigate to="/" />}
                                    />
                                    <Route
                                        path="/zap"
                                        element={<IntegrationsPage />}
                                    />
                                    <Route
                                        path="/maintenance"
                                        element={<MaintenanceDialog />}
                                    />
                                    <Route
                                        path="/expired"
                                        element={
                                            isUserActive ? (
                                                <Navigate to="/" />
                                            ) : (
                                                <ExpiredSubscriptionModal
                                                    loggedInUser={mbxUser}
                                                />
                                            )
                                        }
                                    />
                                    <Route
                                        path="/checkout/reactivate"
                                        element={
                                            <CheckoutApp
                                                monthlyLookupKey={
                                                    "professional-monthly"
                                                }
                                                annualLookupKey={
                                                    "professional-annually"
                                                }
                                                planType={"professional"}
                                                reactivate={true}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/checkout/purchase"
                                        element={
                                            <CheckoutApp
                                                monthlyLookupKey={
                                                    "professional-monthly"
                                                }
                                                annualLookupKey={
                                                    "professional-annually"
                                                }
                                                planType={"professional"}
                                                reactivate={true}
                                            />
                                        }
                                    />
                                    <Route
                                        path="*"
                                        element={<Navigate to="/" />}
                                    />
                                </Route>
                            )}
                            <Route
                                path="/onboarding"
                                element={
                                    <ExternalRedirect to="https://www.gomarketbox.com/" />
                                }
                            />
                            <Route
                                path="/free-trial"
                                element={
                                    <ExternalRedirect to="https://www.gomarketbox.com/" />
                                }
                            />{" "}
                            <Route
                                path="/checkout/basic"
                                element={<Navigate to="/checkout/starter" />}
                            />
                            <Route
                                path="/checkout/starter"
                                element={
                                    <CheckoutApp
                                        monthlyLookupKey={"basic-monthly"}
                                        annualLookupKey={"basic-annually"}
                                        planType={"basic"}
                                    />
                                }
                            />
                            <Route
                                path="/checkout/professional"
                                element={
                                    <CheckoutApp
                                        monthlyLookupKey={
                                            "professional-monthly"
                                        }
                                        annualLookupKey={
                                            "professional-annually"
                                        }
                                        planType={"professional"}
                                    />
                                }
                            />
                            <Route
                                path="/activate-account"
                                element={<ActivateAccountApp />}
                            />
                            <Route
                                path="/login"
                                element={
                                    userIsLoggedIn ? (
                                        <Navigate to="/dashboard" />
                                    ) : (
                                        <>
                                            <CustomSignIn
                                                setMbUserInCache={
                                                    setMbUserInCache
                                                }
                                            />
                                            <Helmet>
                                                <title>
                                                    {`MarketBox \u2014 Login`}
                                                </title>
                                            </Helmet>
                                        </>
                                    )
                                }
                            />
                        </Routes>
                    </ThemeProvider>
                </StyledEngineProvider>
            </>
        </UserContext.Provider>
    );
}

export default App;
